import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Field } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { FormGroup, TextboxField, Modal, SelectDropdownField, validateAddress } from '@tradingblock/components';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { AddressUpdateValueProps, AllStates, Country, getGroupedCountryOptions, State } from '@tradingblock/types';
import { useClearer } from '../../../hooks/useClearer';
import Config from '../../../config';

interface AddressUpdateProps {
  show?: boolean;
  toggleModal: () => void;
  addressType: 'physical' | 'mailing';
  accountHolderIndex: number;
  accountDetails: {
    physicalAddress: {
      address1: string;
      address2?: string;
      city: string;
      country: string;
      postalCode: string;
      state: string;
    };
  };
}

export const AddressUpdate: React.FunctionComponent<AddressUpdateProps> = ({
  show,
  toggleModal,
  addressType,
  accountHolderIndex,
  accountDetails,
}) => {
  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const clearer = useClearer();
  const dispatch = useDispatch();
  const { accountHolders } = useStateSelector(s => s.accountManagement.accountManagementDetails.details);
  const isLoading = useStateSelector(s => s.accountManagement.update.isLoading);
  const user = accountHolders[accountHolderIndex];

  const header = useMemo(() => {
    switch (addressType) {
      case 'physical':
        return 'Physical Address';
      case 'mailing':
        return 'Mailing Address';
      default:
        return '';
    }
  }, [addressType]);

  const initialAddressUpdateValues: AddressUpdateValueProps = useMemo(
    () => ({
      address1:
        addressType === 'physical'
          ? user.address.address1 || ''
          : user.mailingAddress && user.mailingAddress.address1
          ? user.mailingAddress.address1
          : '',
      address2:
        addressType === 'physical'
          ? user.address.address2 || ''
          : user.mailingAddress && user.mailingAddress.address2
          ? user.mailingAddress.address2
          : '',
      city:
        addressType === 'physical'
          ? user.address.city || ''
          : user.mailingAddress && user.mailingAddress.city
          ? user.mailingAddress.city
          : '',
      state:
        addressType === 'physical'
          ? user.address.state || ''
          : user.mailingAddress && user.mailingAddress.state
          ? user.mailingAddress.state
          : '',
      postalCode:
        addressType === 'physical'
          ? user.address.postalCode || ''
          : user.mailingAddress && user.mailingAddress.postalCode
          ? user.mailingAddress.postalCode
          : '',
      country:
        addressType === 'physical'
          ? user.address.country || ''
          : user.mailingAddress && user.mailingAddress.country
          ? user.mailingAddress.country
          : '',
      addressType: '',
    }),
    [addressType, user]
  );

  const getOtherAddressValues = useMemo(() => {
    if (addressType === 'physical' && user.mailingAddress) {
      return {
        address1: user.mailingAddress.address1 || '',
        address2: user.mailingAddress.address2 || '',
        city: user.mailingAddress.city || '',
        state: user.mailingAddress.state || '',
        postalCode: user.mailingAddress.postalCode || '',
        country: user.mailingAddress.country || '',
        addressType,
      };
    } else if (addressType === 'mailing' && user.address) {
      return {
        address1: user.address.address1 || '',
        address2: user.address.address2 || '',
        city: user.address.city || '',
        state: user.address.state || '',
        postalCode: user.address.postalCode || '',
        country: user.address.country || '',
        addressType,
      };
    }
    return null;
  }, [addressType, user]);

  const shouldShowCopyButton = useMemo(() => {
    if (!user) return false;

    const currentAddress = addressType === 'physical' ? user.address : user.mailingAddress;
    const otherAddress = addressType === 'physical' ? user.mailingAddress : user.address;

    if (!currentAddress || !otherAddress) return false;

    // Compare all address fields
    return (
      currentAddress.address1 !== otherAddress.address1 ||
      currentAddress.address2 !== otherAddress.address2 ||
      currentAddress.city !== otherAddress.city ||
      currentAddress.state !== otherAddress.state ||
      currentAddress.postalCode !== otherAddress.postalCode ||
      currentAddress.country !== otherAddress.country
    );
  }, [user, addressType]);

  const onValidate = useCallback(
    (values: AddressUpdateValueProps) => {
      const headerValue = addressType === 'physical' ? 'physical' : 'mailing';

      // Clear USPS storage if address fields are cleared or state is Puerto Rico
      if (!values.address1 || !values.city) {
        localStorage.setItem(`addressVerification-${headerValue}-${accountHolderIndex}`, 'undefined');
      }

      if (values.state === State.PuertoRico) {
        localStorage.setItem(`addressVerification-${headerValue}-${accountHolderIndex}`, 'undefined');
      }

      const addressModel = {
        address1: values.address1 || '',
        address2: values.address2 || '',
        city: values.city || '',
        state: values.state || '',
        postalCode: values.postalCode || '',
        country: values.country || '',
      };

      const errors = validateAddress(addressModel, Config.uspsBaseUrl, Config.uspsId, '');

      if (errors) {
        return Object.keys(errors).reduce(
          (acc, key) => ({
            ...acc,
            [key.replace('address.', '')]: errors[key],
          }),
          {}
        );
      }

      return {};
    },
    [addressType, accountHolderIndex]
  );

  const onSubmit = useCallback(
    (values: AddressUpdateValueProps, { resetForm }) => {
      if (!accountId) return;

      const { address1, address2, city, country, postalCode, state } = values;
      const updateValues = {
        item: addressType === 'physical' ? 'Address' : 'MailingAddress',
        accountHolder: {
          [addressType === 'physical' ? 'address' : 'mailingAddress']: {
            address1,
            address2,
            city,
            country,
            postalCode,
            state,
          },
        },
        holder: accountHolderIndex,
      };

      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId,
          request: updateValues,
        })
      );
      resetForm();
      toggleModal();
    },
    [accountId, addressType, accountHolderIndex, dispatch, toggleModal]
  );

  if (!accountId || !user) {
    return null;
  }

  return (
    <Formik
      initialValues={initialAddressUpdateValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnChange={true}
    >
      {({ handleSubmit, resetForm, values, errors, setValues }) => (
        <Modal setShow={toggleModal} show={show}>
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Address Update</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <h6>{header}</h6>
            <form id="addressUpdate" onSubmit={handleSubmit}>
              <FormGroup>
                <Field
                  component={SelectDropdownField}
                  name="country"
                  id="country"
                  label="Country"
                  options={getGroupedCountryOptions(clearer)}
                  optionsDefaultLabelId="country"
                />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} name="address1" id="address1" label="Address 1" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} name="address2" id="address2" label="Address 2" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} name="city" id="city" label="City" />
              </FormGroup>
              <FormGroup>
                {values.country === Country.UnitedStatesOfAmerica && (
                  <Field
                    component={SelectDropdownField}
                    name="state"
                    id="state"
                    label="State"
                    options={AllStates}
                    optionsDefaultLabelId="state"
                  />
                )}
                {values.country !== Country.UnitedStatesOfAmerica && values.country !== '' && (
                  <Field component={TextboxField} name="state" id="state" label="Province" />
                )}
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} name="postalCode" id="postalCode" label="Postal Code" />
              </FormGroup>
            </form>
            {addressType === 'mailing' && (
              <p className="txt-sm mute">
                All updates are pending admin review. You will be notified when your submission has been processed.
              </p>
            )}
            {shouldShowCopyButton && getOtherAddressValues && (
              <Button
                variant="secondary"
                className="mb-3"
                onClick={() => {
                  const otherAddress = getOtherAddressValues;
                  if (otherAddress) {
                    setValues(otherAddress);
                  }
                }}
              >
                Copy from {addressType === 'physical' ? 'Mailing' : 'Physical'} Address
              </Button>
            )}
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="modal-footer-justified">
            <Button
              variant="secondary"
              onClick={() => {
                resetForm();
                toggleModal();
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={() => handleSubmit()} disabled={isLoading}>
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
